import type { CompetitorAd, CompetitorScrapeState, CompetitorView } from '../client'
import { PLATFORM_ICONS } from './platformIcons'

export type MediaFilter = 'all' | 'static' | 'video'

export type AdFilters = {
  selected: Set<string>
  showInactive: boolean
  media: MediaFilter
  favouritesOnly: boolean
}

export function platform(name: string) {
  const lower = name.toLowerCase()
  return PLATFORM_ICONS.find(({ match }) => lower.includes(match)) ?? PLATFORM_ICONS[PLATFORM_ICONS.length - 1]
}

export function isVideoAd(ad: CompetitorAd) {
  return (ad.cards ?? []).some((card) => Boolean(card.video_url))
}

export function isScraping({ apify_status, ingestion_in_progress }: CompetitorScrapeState) {
  return apify_status === 'pending' || apify_status === 'running' || Boolean(ingestion_in_progress)
}

export function isBusy({ active_scrape, inactive_scrape }: CompetitorView) {
  return isScraping(active_scrape) || isScraping(inactive_scrape)
}

export function scrapeLabel(competitor: CompetitorView) {
  if (isBusy(competitor)) return 'Updating…'

  const scrapedAt = competitor.active_scrape.last_scraped_at
  if (!scrapedAt) return 'Not yet updated'

  const date = new Date(scrapedAt)
  if (Number.isNaN(date.getTime())) return 'Last updated'

  return `Last updated ${date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`
}

export function ageRange({ age_min, age_max }: CompetitorAd) {
  if (age_min == null && age_max == null) return ''
  if (age_min != null && age_max != null) return `${age_min}–${age_max}`
  return String(age_min ?? age_max)
}

export function runningTime(ad: CompetitorAd) {
  if (!ad.start_date) return ''

  const started = new Date(ad.start_date)
  if (Number.isNaN(started.getTime())) return ''

  const end = ad.end_date ? new Date(ad.end_date) : new Date()
  const days = Math.max(0, Math.round((end.getTime() - started.getTime()) / 86_400_000))
  if (days < 1) return 'today'
  if (days < 31) return `< ${days} days`

  const months = Math.round(days / 30)
  return months < 12 ? `< ${months} months` : `> ${Math.floor(months / 12)} years`
}

/** Backend order is impressions-descending per competitor; keep it so cards
 * don't reshuffle under the reader between polls. */
export function mergeAds(competitors: CompetitorView[], filters: AdFilters) {
  const { selected, showInactive, media, favouritesOnly } = filters

  return competitors
    .filter(({ page_id }) => favouritesOnly || selected.has(page_id))
    .flatMap(({ ads }) =>
      (ads ?? []).filter((ad) => {
        if (favouritesOnly ? !ad.is_favourite : !showInactive && !ad.is_active) return false
        if (media === 'static') return !isVideoAd(ad)
        if (media === 'video') return isVideoAd(ad)
        return true
      }),
    )

}
